import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ArrowRight, Plus } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { KpiCard, SearchField, TablePagination } from '../../../components/design-system/admin';
import { CrmContactsQuery, useCrmApi } from '../../../common/hooks/use-crm-api.hook';
import {
  CONTACT_SOURCES, CONTACT_STATUS, CrmContact, CrmContactsPage, CrmLead, FLAGS,
} from '../../../common/models/crm.model';
import { CrmField, CrmRecordSheet } from './crm-record-sheet';

const PAGE_SIZE = 50;

const CONTACT_FIELDS: CrmField[] = [
  { key: 'company', label: 'ADMIN_CRM.FIELDS.COMPANY' },
  { key: 'contact', label: 'ADMIN_CRM.FIELDS.CONTACT' },
  { key: 'title', label: 'ADMIN_CRM.FIELDS.TITLE_ROLE' },
  { key: 'email', label: 'ADMIN_CRM.FIELDS.EMAIL' },
  { key: 'country', label: 'ADMIN_CRM.FIELDS.COUNTRY', type: 'country' },
  { key: 'status', label: 'ADMIN_CRM.FIELDS.STATUS', type: 'select', options: CONTACT_STATUS },
  { key: 'source', label: 'ADMIN_CRM.FIELDS.SOURCE' },
  { key: 'campaign', label: 'ADMIN_CRM.FIELDS.CAMPAIGN' },
  { key: 'notes', label: 'ADMIN_CRM.FIELDS.NOTES_EXCHANGES', type: 'textarea' },
];

/** The outreach base (1,400+ contacts): filters, per-status counts, paged table, promote to pipeline. */
export const CrmContactsTab: React.FC = () => {
  const { t } = useTranslation();
  const api = useCrmApi();
  const [data, setData] = useState<CrmContactsPage | null>(null);
  const [filters, setFilters] = useState<Required<Omit<CrmContactsQuery, 'page'>>>({
    q: '', source: '', campaign: '', status: '', country: '',
  });
  const [page, setPage] = useState(0);
  const [editing, setEditing] = useState<Partial<CrmContact> | null>(null);
  const debounce = useRef<ReturnType<typeof setTimeout> | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await api.listContacts({ ...filters, page }));
    } catch {
      toast.error(t('ADMIN_CRM.CONTACTS.LOAD_FAILED'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, page]);

  // Typing in the search refetches once the user pauses.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    debounce.current = setTimeout(load, 200);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [load]);

  const setFilter = (key: keyof typeof filters) => (value: string) => {
    setFilters((f) => ({ ...f, [key]: value }));
    setPage(0);
  };

  const updateStatus = async (c: CrmContact, status: string) => {
    try {
      await api.update<CrmContact>('contacts', c._id, { status });
      load();
    } catch {
      toast.error(t('ADMIN_CRM.COMMON.UPDATE_FAILED'));
    }
  };

  const save = async (form: Partial<CrmContact>) => {
    try {
      const { _id, ...fields } = form;
      if (_id) await api.update<CrmContact>('contacts', _id, fields);
      else await api.create<CrmContact>('contacts', fields);
      setEditing(null);
      toast.success(t('ADMIN_CRM.CONTACTS.SAVED'));
      load();
    } catch (e: any) {
      toast.error(e?.message || t('ADMIN_CRM.COMMON.SAVE_FAILED'));
    }
  };

  const remove = async (id: string) => {
    try {
      await api.remove('contacts', id);
      setEditing(null);
      load();
    } catch {
      toast.error(t('ADMIN_CRM.COMMON.DELETE_FAILED'));
    }
  };

  // Promote a contact to the leads pipeline, unless its company is already there.
  const promote = async (c: CrmContact) => {
    try {
      const leads = await api.list<CrmLead>('leads');
      if ((leads || []).some((l) => l.company.toLowerCase() === (c.company || '').toLowerCase())) {
        toast.info(t('ADMIN_CRM.CONTACTS.ALREADY', { company: c.company }));
        return;
      }
      await api.create<CrmLead>('leads', {
        company: c.company || c.email,
        contact: c.contact + (c.title ? ` · ${c.title}` : ''),
        email: c.email,
        country: c.country,
        stage: c.status === 'interesse' ? 'interested' : 'contacted',
        notes: `${t('ADMIN_CRM.CONTACTS.PROMOTED_NOTE', { source: `${c.source}${c.campaign ? ` · ${c.campaign}` : ''}` })}${c.notes ? ` ${c.notes}` : ''}`,
      });
      toast.success(t('ADMIN_CRM.CONTACTS.PROMOTED', { company: c.company }));
    } catch {
      toast.error(t('ADMIN_CRM.CONTACTS.PROMOTE_FAILED'));
    }
  };

  const bs = data?.byStatus || {};
  const sum = Object.values(bs).reduce((a, b) => a + b, 0);
  const touched = sum - (bs.non_contacte || 0);
  const replies = (bs.repondu || 0) + (bs.interesse || 0);

  return (
    <div className="flex flex-col gap-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
        <KpiCard loading={!data} label={t('ADMIN_CRM.CONTACTS.KPI.SELECTION')} value={sum} hint={t('ADMIN_CRM.CONTACTS.KPI.SELECTION_HINT')} />
        <KpiCard loading={!data} label={t('ADMIN_CRM.CONTACTS.KPI.CONTACTED')} value={touched} hint={t('ADMIN_CRM.CONTACTS.KPI.CONTACTED_HINT')} />
        <KpiCard loading={!data} label={t('ADMIN_CRM.CONTACTS.KPI.REPLIES')} value={<span className="text-success">{replies}</span>} hint={t('ADMIN_CRM.CONTACTS.KPI.REPLIES_HINT')} />
        <KpiCard loading={!data} label={t('ADMIN_CRM.CONTACTS.KPI.REFUSED')} value={<span className="text-destructive">{bs.refus || 0}</span>} hint={t('ADMIN_CRM.CONTACTS.KPI.REFUSED_HINT')} />
        <KpiCard loading={!data} label={t('ADMIN_CRM.CONTACTS.KPI.NOT_CONTACTED')} value={<span className="text-warning">{bs.non_contacte || 0}</span>} hint={t('ADMIN_CRM.CONTACTS.KPI.NOT_CONTACTED_HINT')} />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <SearchField value={filters.q} onChange={setFilter('q')} placeholder={t('ADMIN_CRM.CONTACTS.SEARCH')} className="w-full sm:w-72" />
        <NativeSelect value={filters.source} onChange={(e) => setFilter('source')(e.target.value)} aria-label={t('ADMIN_CRM.CONTACTS.SOURCE')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.CONTACTS.ALL_SOURCES')}</NativeSelectOption>
          {Object.entries(CONTACT_SOURCES).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
        </NativeSelect>
        <NativeSelect value={filters.campaign} onChange={(e) => setFilter('campaign')(e.target.value)} aria-label={t('ADMIN_CRM.CONTACTS.CAMPAIGN')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.CONTACTS.ALL_CAMPAIGNS')}</NativeSelectOption>
          {data?.campaigns.map((c) => <NativeSelectOption key={c} value={c}>{c}</NativeSelectOption>)}
        </NativeSelect>
        <NativeSelect value={filters.status} onChange={(e) => setFilter('status')(e.target.value)} aria-label={t('ADMIN_CRM.COMMON.STATUS')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.CONTACTS.ALL_STATUSES')}</NativeSelectOption>
          <NativeSelectOption value="touched">{t('ADMIN_CRM.CONTACTS.TOUCHED')}</NativeSelectOption>
          {Object.entries(CONTACT_STATUS).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
        </NativeSelect>
        <NativeSelect value={filters.country} onChange={(e) => setFilter('country')(e.target.value)} aria-label={t('ADMIN_CRM.COMMON.COUNTRY')}>
          <NativeSelectOption value="">{t('ADMIN_CRM.COMMON.ALL_COUNTRIES')}</NativeSelectOption>
          {data?.countries.map((c) => <NativeSelectOption key={c} value={c}>{FLAGS[c] || ''} {c}</NativeSelectOption>)}
        </NativeSelect>
        <span className="flex-1" />
        <Button onClick={() => setEditing({ status: 'contacte', source: 'manuel initial', country: 'FR' })}><Plus />{t('ADMIN_CRM.CONTACTS.NEW')}</Button>
      </div>

      <div className="rounded-xl border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{t('ADMIN_CRM.CONTACTS.COL.COMPANY')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CONTACTS.COL.CONTACT')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CONTACTS.COL.COUNTRY')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CONTACTS.COL.SOURCE')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CONTACTS.COL.STATUS')}</TableHead>
              <TableHead>{t('ADMIN_CRM.CONTACTS.COL.NOTES')}</TableHead>
              <TableHead className="w-10" />
            </TableRow>
          </TableHeader>
          <TableBody>
            {data?.items.map((c) => (
              <TableRow key={c._id} className="cursor-pointer" onClick={() => setEditing(c)}>
                <TableCell className="font-medium">{c.company}</TableCell>
                <TableCell>
                  <div>{c.contact}</div>
                  <div className="text-xs text-muted-foreground">{c.email}</div>
                </TableCell>
                <TableCell>{FLAGS[c.country] || ''} {c.country}</TableCell>
                <TableCell className="text-xs text-muted-foreground">{c.source}</TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <NativeSelect size="sm" value={c.status} onChange={(e) => updateStatus(c, e.target.value)} aria-label={t('ADMIN_CRM.CONTACTS.STATUS_ARIA')}>
                    {Object.entries(CONTACT_STATUS).map(([k, v]) => <NativeSelectOption key={k} value={k}>{t(v)}</NativeSelectOption>)}
                  </NativeSelect>
                </TableCell>
                <TableCell className="max-w-[380px] text-xs whitespace-normal text-muted-foreground">
                  {(c.notes || '').length > 160 ? `${c.notes.slice(0, 160)}…` : c.notes}
                </TableCell>
                <TableCell onClick={(e) => e.stopPropagation()}>
                  <Button size="icon-sm" variant="ghost" title={t('ADMIN_CRM.CONTACTS.PROMOTE')} aria-label={t('ADMIN_CRM.CONTACTS.PROMOTE')} onClick={() => promote(c)}>
                    <ArrowRight />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && (
          <TablePagination page={page} pageSize={PAGE_SIZE} total={data.total} onPageChange={setPage} className="border-t" />
        )}
      </div>

      <CrmRecordSheet<CrmContact>
        record={editing}
        fields={CONTACT_FIELDS}
        required="email"
        title={{ create: t('ADMIN_CRM.CONTACTS.NEW'), edit: t('ADMIN_CRM.CONTACTS.EDIT') }}
        onSave={save}
        onDelete={remove}
        onClose={() => setEditing(null)}
      />
    </div>
  );
};
